/**
 * Tags Management Page
 *
 * Allows admins to manage course and user tags.
 * Features: tabs for course/user tags, paged table view, workspace filter, CRUD operations.
 */

import { useSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Tag, TagType, UserRole, Workspace } from '../apis/api-client';
import { tagsClient } from '../apis/client-instances';
import PageHeader from '../components/common/PageHeader';
import PagedTable, { PagedTableColumn } from '../components/common/PagedTable';
import WorkspaceAutocomplete from '../components/common/WorkspaceAutocomplete';
import ConfirmDialog from '../components/ConfirmDialog';
import { AddIcon, DeleteIcon, EditIcon, LabelIcon, SearchIcon } from '../components/icons';
import EditTagDialog from '../components/tags/EditTagDialog';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '../components/ui';
import { alert } from '../hooks/function';
import { usePagedList } from '../hooks/usePagedList';
import { RootState } from '../types';
import { formatDateTime, isAccountOrSystemAdmin } from '../utils/roleHelpers';

export default function TagsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const userRole = currentUser?.role;
  // Tags are AccountAdmin/Admin only — server enforces this too, but the
  // client redirect avoids loading the page just to show a stack of 401s.
  // Computed here so the early return below stays after all hook calls.
  const isAuthorizedForTags = !currentUser || isAccountOrSystemAdmin(userRole);

  const [tabValue, setTabValue] = useState(0);
  const [pageWorkspaceFilter, setPageWorkspaceFilter] = useState<Workspace | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const isAccountAdmin = isAccountOrSystemAdmin(userRole);

  // Global switcher locks the workspace scope when set. Page filter only
  // applies when the switcher is "All Workspaces" (null).
  const switcherWorkspace = useSelector((state: RootState) => state.workspaces.selectedWorkspace);
  const effectiveWorkspaceId = (switcherWorkspace?.id ?? pageWorkspaceFilter?.id) ?? null;
  const showPageFilter = isAccountAdmin && !switcherWorkspace;

  const tagType = tabValue === 0 ? TagType.CourseTag : TagType.UserTag;

  const paged = usePagedList<Tag>({
    fetcher: useCallback(
      async ({ page, pageSize, search }) => {
        const result = await tagsClient.searchTags(
          search || undefined,
          tagType,
          effectiveWorkspaceId ?? undefined,
          false, // includeAccountWide — admin page filters to exact workspace match
          false, // accountWideOnly
          undefined, // ids
          page,
          pageSize
        );
        return {
          items: result.items ?? [],
          totalCount: result.totalCount ?? 0,
          page: result.page ?? page,
          pageSize: result.pageSize ?? pageSize,
        };
      },
      [tagType, effectiveWorkspaceId]
    ),
    deps: [tagType, effectiveWorkspaceId ?? ''],
    onError: () => alert(enqueueSnackbar, 'Failed to load tags', 'error'),
  });

  /**
   * Check if current user can edit/delete a tag
   * - Admin/AccountAdmin: Can edit any tag
   * - WorkspaceAdmin: Can only edit workspace-specific tags (not account-wide)
   */
  const canModifyTag = (tag: Tag): boolean => {
    if (userRole === UserRole.Admin || userRole === UserRole.AccountAdmin) return true;
    if (userRole === UserRole.WorkspaceAdmin) {
      if (!tag.workspaceId) return false;
      return tag.workspaceId === currentUser?.workspaceId;
    }
    return false;
  };

  const getDisabledTooltip = (tag: Tag): string => {
    if (userRole === UserRole.WorkspaceAdmin && !tag.workspaceId) {
      return 'Account-wide tags can only be edited by Super Admins';
    }
    return 'You do not have permission to modify this tag';
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddTag = () => {
    setSelectedTag(null);
    setIsCreatingNew(true);
    setEditDialogOpen(true);
  };

  const handleEditTag = (tag: Tag) => {
    setSelectedTag(tag);
    setIsCreatingNew(false);
    setEditDialogOpen(true);
  };

  const handleDeleteTag = (tag: Tag) => {
    setSelectedTag(tag);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTag?.id) return;

    try {
      await tagsClient.deleteTag(selectedTag.id);
      alert(enqueueSnackbar, 'Tag deleted successfully', 'success');
      paged.refresh();
    } catch (error: any) {
      alert(enqueueSnackbar, error?.response?.data || 'Failed to delete tag', 'error');
    } finally {
      setConfirmDialogOpen(false);
      setSelectedTag(null);
    }
  };

  const handleSaveTag = async (tagData: { name: string; workspaceId?: number | null }) => {
    try {
      if (isCreatingNew) {
        await tagsClient.createTag({
          name: tagData.name,
          tagType,
          workspaceId: tagData.workspaceId || undefined,
        });
        alert(enqueueSnackbar, 'Tag created successfully', 'success');
      } else if (selectedTag?.id) {
        await tagsClient.updateTag(selectedTag.id, {
          name: tagData.name,
          tagType: selectedTag.tagType,
          workspaceId: tagData.workspaceId || undefined,
        });
        alert(enqueueSnackbar, 'Tag updated successfully', 'success');
      }
      paged.refresh();
      setEditDialogOpen(false);
    } catch (error: any) {
      alert(enqueueSnackbar, error?.response?.data || 'Failed to save tag', 'error');
    }
  };

  const getTagLevel = (tag: Tag) => (tag.workspaceId ? 'Workspace' : 'Account');

  const getWorkspaceName = (tag: Tag) => {
    if (!tag.workspaceId) return '—';
    if (switcherWorkspace?.id === tag.workspaceId) return switcherWorkspace.name ?? `#${tag.workspaceId}`;
    if (pageWorkspaceFilter?.id === tag.workspaceId) return pageWorkspaceFilter.name ?? `#${tag.workspaceId}`;
    return (tag as any).workspaceName ?? `#${tag.workspaceId}`;
  };

  const columns: PagedTableColumn<Tag>[] = [
    {
      key: 'name',
      label: 'Tag Name',
      render: (tag) => <Chip label={tag.name} size="small" color="primary" variant="outlined" />,
    },
    {
      key: 'level',
      label: 'Level',
      // Pastel pill — soft tinted fill + deeper-tone text instead of the
      // saturated MUI default. Two distinct hues so Account vs Workspace
      // is still a glance-level read: cyan for Account (matches brand),
      // lavender for Workspace.
      render: (tag) => {
        const isAccount = !tag.workspaceId;
        const pastel = isAccount
          ? { bg: '#e3f7fd', border: '#bce5f1', fg: '#0a6e8c' }
          : { bg: '#eef1fb', border: '#d2d9f2', fg: '#3c4ba0' };
        return (
          <Chip
            label={getTagLevel(tag)}
            size="small"
            sx={{
              bgcolor: pastel.bg,
              color: pastel.fg,
              border: '1px solid',
              borderColor: pastel.border,
              fontWeight: 600,
              letterSpacing: '0.02em',
              '& .MuiChip-label': { px: 1.1 },
            }}
          />
        );
      },
    },
    { key: 'workspace', label: 'Workspace', render: (tag) => getWorkspaceName(tag) },
    { key: 'createdAt', label: 'Created', render: (tag) => formatDateTime(tag.createdAt) },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (tag) => (
        <>
          <Tooltip title={canModifyTag(tag) ? 'Edit' : getDisabledTooltip(tag)}>
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditTag(tag);
                }}
                disabled={!canModifyTag(tag)}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canModifyTag(tag) ? 'Delete' : getDisabledTooltip(tag)}>
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTag(tag);
                }}
                disabled={!canModifyTag(tag)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ),
    },
  ];

  // Bounce non-admins after all hooks have run.
  if (!isAuthorizedForTags) {
    return <Navigate to="/courses" replace />;
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'white' }}>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ p: 4 }}>
          <PageHeader
            icon={<LabelIcon />}
            title="Tags"
            description="Curate course and user tags for filtering, reporting, and bulk targeting."
            count={paged.totalCount}
            actions={
              <>
                <TextField
                  size="small"
                  placeholder={`Search ${tabValue === 0 ? 'course' : 'user'} tags...`}
                  value={paged.search ?? ''}
                  onChange={(e) => paged.setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 280 }}
                />
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleAddTag}
                  sx={{ px: 3 }}
                >
                  New Tag
                </Button>
              </>
            }
          />

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Course Tags" />
              <Tab label="User Tags" />
            </Tabs>
          </Box>

          <Box sx={{ pt: 3 }}>
            {showPageFilter && (
              <Box sx={{ mb: 3 }}>
                <WorkspaceAutocomplete
                  value={pageWorkspaceFilter}
                  onChange={setPageWorkspaceFilter}
                />
              </Box>
            )}

            <PagedTable<Tag>
              columns={columns}
              rows={paged.items}
              totalCount={paged.totalCount}
              page={paged.page}
              pageSize={paged.pageSize}
              loading={paged.loading}
              rowKey={(t) => t.id ?? t.name ?? Math.random()}
              onPageChange={paged.setPage}
              onPageSizeChange={paged.setPageSize}
              onRowClick={(tag) => canModifyTag(tag) && handleEditTag(tag)}
              emptyMessage={`No ${tabValue === 0 ? 'course' : 'user'} tags found`}
            />
          </Box>
        </Box>

        <EditTagDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSave={handleSaveTag}
          tag={selectedTag}
          isAccountAdmin={isAccountAdmin}
        />

        <ConfirmDialog
          open={confirmDialogOpen}
          title="Delete Tag"
          content={`Are you sure you want to delete the tag "${selectedTag?.name}"?`}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setConfirmDialogOpen(false);
            setSelectedTag(null);
          }}
        />
      </Box>
    </Box>
  );
}
